import React, { useEffect, useState } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { Lightbulb } from "lucide-react"

type LoaderVariant = "csv" | "audio" | "script"

const VARIANT_LABELS: Record<LoaderVariant, string> = {
  csv: "Importing test cases",
  audio: "Uploading audio files",
  script: "Creating scripted test cases",
}

const TIPS = [
  "Resolution rate is the north star — leading deployments hit 75-85% without human intervention.",
  "Demo performance ≠ production performance. Always demand production metrics.",
  "Customers don't hang up because it's a bot — they hang up because it's not helping fast enough.",
  "Lead with context: 'Are you calling about your order?' beats 'How can I help you?' every time.",
  "Target sub-500ms context lookup so your agent has the info before it speaks.",
  "Track post-escalation outcomes. A botched handoff undoes all the goodwill your AI built.",
  "Cost per resolution matters more than cost per minute to your C-suite.",
  "Test with 100+ accents and real-world noise — not just quiet conference rooms.",
  "High resolution rate + poor satisfaction = a ticking time bomb for churn.",
  "Track first-call resolution, not just deflection. Solved problems > deflected calls.",
  "Every call is a data point. The best teams build feedback loops that compound over time.",
  "Invest in business logic over audio quality. Predict why users call, not just that they called.",
  "The gap between voice AI leaders and laggards? Systematic testing — not better models.",
  "Drop-off rates decline when you deliver value fast — not when your AI sounds more human.",
  "Speed to value beats sounding human. Solve the problem before they decide to hang up.",
]

interface GeneratingLoaderProps {
  variant?: LoaderVariant
}

export const GeneratingLoader: React.FC<GeneratingLoaderProps> = ({
  variant = "csv",
}) => {
  const [tipIndex, setTipIndex] = useState(() =>
    Math.floor(Math.random() * TIPS.length),
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % TIPS.length)
    }, 15000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center max-w-2xl w-full px-4">
        <div className="text-center mb-3">
          <span className="text-3xl select-none bg-gradient-to-r from-foreground from-0% via-coval-text-muted via-50% to-foreground to-100% bg-[length:300%_auto] animate-[shimmer_2s_linear_infinite] bg-clip-text text-transparent">
            {VARIANT_LABELS[variant]}
          </span>
        </div>
        <div className="w-full h-[48px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={tipIndex}
              className="flex items-center justify-center gap-1.5 text-sm text-coval-text-muted text-center leading-relaxed"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <Lightbulb className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">{TIPS[tipIndex]}</span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
